import axios from 'axios';

const defaultAxiosConfig = (accessToken) => ({
  headers: {
    'Content-Type': 'application/json',
    Authorization: accessToken
  }
});

class AdminChatApi {
  async getChats(params = {}) {
    const accessToken = window.localStorage.getItem('accessToken');
    const queryParams = new URLSearchParams();

    // Add query parameters if they exist
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.type) queryParams.append('type', params.type);
    if (params.exclude_types && Array.isArray(params.exclude_types)) {
      params.exclude_types.forEach((t) => queryParams.append('exclude_type', t));
    } else if (params.exclude_type) {
      queryParams.append('exclude_type', params.exclude_type);
    }
    if (params.status) queryParams.append('status', params.status);
    if (params.needs_response !== undefined && params.needs_response !== null) {
      queryParams.append('needs_response', params.needs_response);
    }
    if (params.admin_id) queryParams.append('admin_id', params.admin_id);
    if (params.participant_id) queryParams.append('participant_id', params.participant_id);
    // Some backends expect patient_id instead of participant_id for user-scoped filters.
    // Send both when provided so either contract will work.
    if (params.patient_id) queryParams.append('patient_id', params.patient_id);
    if (params.chat_id) queryParams.append('chat_id', params.chat_id);
    if (params.patient_email) queryParams.append('patient_email', params.patient_email);
    if (params.patient_name) queryParams.append('patient_name', params.patient_name);
    if (params.coach_id) queryParams.append('coach_id', params.coach_id);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.responder) queryParams.append('responder', params.responder);
    if (params.search) queryParams.append('search', params.search);
    if (params.order_by) queryParams.append('order_by', params.order_by);
    if (params.order_direction) queryParams.append('order_direction', params.order_direction);
    if (params.mentioned_me === true || params.mentioned_me === 'true' || params.mentioned_me === 1 || params.mentioned_me === '1') {
      queryParams.append('mentioned_me', 'true');
    }

    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chats?${queryParams.toString()}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async updateChat(chatId, data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'PUT',
      url: `${process.env.REACT_APP_BACKEND_URL}/chat/${chatId}`,
      data,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async generateAiDraft(data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/ai-drafts`,
      data,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /** Chats > Tools: queue a debug draft for a past patient message, as the given agent. */
  async startDraftDebugRun({ chat_id, message_id, admin_id }) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/ai-drafts/debug`,
      data: { chat_id, message_id, admin_id },
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /** Poll until completed_at is set; then result or error is filled. */
  async getDraftDebugRun(id) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/ai-drafts/debug/${id}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async polishAiDraft(draft) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/ai-drafts/polish`,
      data: { draft },
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /**
   * Waiting-response tallies for the sidebar badge.
   * Prefer this over getChats({ limit: 1 }): it skips the paginated chat page
   * and the filtered COUNT(*) that the full chat-list endpoint also runs.
   * @returns {Promise<{ waiting_response_counts: { consultations: number, coaching: number, support: number }, my_assigned_count: number | null }>}
   */
  async getWaitingCounts() {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chats/waiting-counts`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /**
   * Count a user's open (non-closed) support chats.
   * Used to decide whether closing a support chat should warn about the
   * user's other open support chats.
   * @param {string} userId - the patient's user ID
   * @returns {Promise<{ user_id: string, count: number, chats: Array<{ id: string, status: string, conversation_subject: string | null }> }>}
   */
  async getOpenSupportChatsCount(userId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chats/open-count?user_id=${encodeURIComponent(userId)}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /**
   * Close every open support chat for a user.
   * @param {string} userId - the patient's user ID
   * @returns {Promise<{ closed_count: number, chat_ids: string[] }>}
   */
  async bulkCloseSupportChats(userId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chats/bulk-close`,
      data: { user_id: userId },
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /**
   * Close a specific set of selected support chats. The selection can span
   * multiple users (Gmail-style multi-select). Ids that are not open support
   * chats are ignored by the backend, so a stale selection is safe.
   * @param {string[]} chatIds - the chat IDs to close
   * @returns {Promise<{ closed_count: number, chat_ids: string[] }>}
   */
  async bulkCloseSupportChatsByIds(chatIds) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chats/bulk-close-by-ids`,
      data: { chat_ids: chatIds },
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /**
   * Edit, publish, or delete a chat message (e.g. ai_draft).
   * @param {string} messageId
   * @param {{ action: 'edit'|'publish'|'delete', message?: string }} data - message required for action 'edit'
   */
  async editChatMessage(messageId, data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'PUT',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat-message/${messageId}`,
      data,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async getChatByMessageId(messageId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/by-message/${messageId}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async getChatMessages(chatId, params = {}) {
    const accessToken = window.localStorage.getItem('accessToken');
    const queryParams = new URLSearchParams();
    if (params.after) queryParams.append('after', params.after);
    if (params.limit) queryParams.append('limit', params.limit);

    const query = queryParams.toString();
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/messages${query ? `?${query}` : ''}`,
      signal: params.signal,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async sendChatMessage(chatId, data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/messages`,
      data,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async getScheduledChatMessages(chatId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/messages/scheduled`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async rescheduleScheduledChatMessage(chatId, id, { scheduled_for, timezone }) {
    const accessToken = window.localStorage.getItem('accessToken');
    const data = {};

    if (scheduled_for) data.scheduled_for = scheduled_for;
    if (timezone) data.timezone = timezone;

    const response = await axios({
      method: 'PUT',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/messages/scheduled/${id}`,
      data,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async cancelScheduledChatMessage(chatId, id) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'DELETE',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/messages/scheduled/${id}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async markChatRead(chatId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/read`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async getSupportChats(userId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chats/support/${userId}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  /**
   * Staff-only note on a chat thread (does not notify patient or change chat status).
   * @param {string} chatId
   * @param {{ message: string, mentions?: string[], priority?: 'low'|'normal'|'high'|'urgent', noteCategory?: 'routing'|'context'|'action_required' }} data
   * @returns {Promise<{ success: boolean, messageId: string, chatId: string }>}
   */
  /**
   * Create a new support chat for a patient.
   * @param {string} userId - the patient's user ID
   * @param {string} [conversationSubject] - optional subject line
   * @returns {Promise<{ id: string, created: boolean }>}
   */
  async createSupportChat(userId, conversationSubject) {
    const accessToken = window.localStorage.getItem('accessToken');
    const body = { user_id: userId };
    if (conversationSubject) body.conversation_subject = conversationSubject;
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/support`,
      data: body,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }

  /**
   * Create (or return the open) IOP chat for an IOP lead.
   * @param {string} userId - the patient's user ID
   * @param {string} [therapistId] - defaults server-side to the episode clinician
   * @returns {Promise<{ id: string, created: boolean }>}
   */
  async createIopChat(userId, therapistId) {
    const accessToken = window.localStorage.getItem('accessToken');
    const body = { user_id: userId };
    if (therapistId) body.therapist_id = therapistId;
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/iop`,
      data: body,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }

  async createInternalChatNote(chatId, data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const body = { message: data.message };
    if (data.priority != null) body.priority = data.priority;
    if (data.noteCategory != null) body.noteCategory = data.noteCategory;
    if (Array.isArray(data.mentions) && data.mentions.length > 0) {
      body.mentions = data.mentions;
    }
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat/${chatId}/internal-notes`,
      data: body,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }

  async getChatConfig(type = 'support') {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat-configs/${type}`,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async updateChatConfig(type, data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'PUT',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/chat-configs/${type}`,
      data,
      ...defaultAxiosConfig(accessToken)
    });

    return response.data;
  }

  async getClinicianChatBlackouts() {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'GET',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/clinician-chat-blackouts`,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }

  async createClinicianChatBlackout(data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'POST',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/clinician-chat-blackouts`,
      data,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }

  async updateClinicianChatBlackout(id, data) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'PUT',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/clinician-chat-blackouts/${id}`,
      data,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }

  async deleteClinicianChatBlackout(id) {
    const accessToken = window.localStorage.getItem('accessToken');
    const response = await axios({
      method: 'DELETE',
      url: `${process.env.REACT_APP_BACKEND_URL}/admin/clinician-chat-blackouts/${id}`,
      ...defaultAxiosConfig(accessToken)
    });
    return response.data;
  }
}

export const adminChatApi = new AdminChatApi();
